import { useCallback, useEffect, useRef } from 'react';

import { useOnBeforeUnload } from '@ctct/presentation-layer';

export interface WidgetLifecycle {
  onMount?(): void;
  onUnmountOrUnload?(eventName: 'unmount' | 'unload'): void;
}

export function useWidgetLifecycle(status: string, lifecycle?: null | WidgetLifecycle, lifecycleKey?: unknown) {
  const ref = useRef(lifecycle);

  // Ordinarily, an assignment like this during the render would be disallowed!
  // However, it's the only way to guarantee that the value is set before any other handlers
  // are run, unless we want to import `useLatestRef` from the "react-use" library.
  ref.current = lifecycle;

  useEffect(() => {
    if (status !== 'loaded') return undefined;

    const methods = ref.current;

    if (!methods) return undefined;

    methods.onMount?.();

    return () => {
      methods.onUnmountOrUnload?.('unmount');
    };
  }, [status, lifecycleKey]);

  const handleUnload = useCallback(() => {
    if (status !== 'loaded') return;

    ref.current?.onUnmountOrUnload?.('unload');
  }, [status, lifecycleKey]);

  useOnBeforeUnload(handleUnload);
}
